// Elf calls: a short call with Pipa at the North Pole switchboard. The daily "sparkle mission" is
// chosen before the call, so the family's device can remember it and Pipa can ask about it next
// time. Written in English for Pipa's instructions; she speaks the call's language.

export const MISSIONS = [
  'help set the table for dinner',
  'draw a picture for someone you love and give it to them',
  'say a big thank you to three different people today',
  'tidy up your toys before bedtime without being asked',
  'share a snack with someone',
  'give a grown-up a big hug and tell them one thing you like about them',
  'make someone laugh today',
  'help feed a pet or water a plant',
  'be extra kind to a brother, sister or friend all day',
  'put your shoes neatly by the door every time you come in',
];

export const ELF_CALL_SECONDS = 4 * 60;

/** The one-minute warning: make sure the mission and the message for Santa happen. */
/** The child has gone quiet: Pipa leads on to the next part of her call. */
export function elfQuietNudge(name: string, again: boolean) {
  return `[ELVES: ${name} has gone quiet${again ? ' again' : ''}. That is fine: little ones often do. Do not wait and never ask if they are still there; lead on, bubbly and warm.${again ? ' Do something they can just listen to or giggle at: a funny bit of workshop news, or a silly sound from the switchboard.' : ' Go on to the next part of your call that you have not done yet (the Nice List check, your game, the sparkle mission, the message for Santa), or ask an easy yes-or-no question.'}]`;
}

export const ELF_WRAP_UP = '[ELVES: About one minute left. If you have not yet, give the sparkle mission and ask for a message for Santa now, briefly. No new topics, and do NOT say goodbye yet; the elves will tell you when.]';

export function pickMission(previous?: string): string {
  const choices = MISSIONS.filter((m) => m !== previous);
  return choices[Math.floor(Math.random() * choices.length)];
}

/** What Pipa hears when the child taps something in her room. */
export const ELF_TAPS: Record<string, string> = {
  list: 'The child just tapped the Nice List book and it glowed gold.',
  switchboard: 'The child just tapped your switchboard and all its little lamps lit up.',
  letters: 'The child just tapped the mail sack and letters fluttered out of it.',
  tree: 'The child tapped the little Christmas tree and its candles twinkled.',
  lamp: 'The child tapped your desk lamp and it flared up.',
  aurora: 'The child tapped the window and the northern lights flared up.',
  pipa: 'The child just poked you through the screen, and it tickled!',
};

// Each elf call gets its own little plan, so calling every day stays fresh.
export const ELF_OPENINGS = [
  'You answer mid-task, a bit out of breath, because a whole sack of letters just tipped over: "Oh! Hello! Sorry, sorry, letters everywhere!"',
  'You have the hiccups from drinking your cocoa too fast. They come back a few times during the call and you giggle about it.',
  'Rudolph is nibbling the switchboard cord again and you have to shoo him away ("Rudolph, no! That\'s not a carrot!").',
  'You were just testing a squeaky toy duck for the workshop, and it squeaks once more by accident when you pick up the phone.',
  'You answer very properly, like a real operator ("Connection number {n}, North Pole switchboard!"), then can\'t keep it up and burst into giggles.',
];

export const ELF_GAMES = [
  'THE ELF TEST: the child can become an honorary elf by passing three silly tests, one at a time: their best reindeer noise, their favourite Christmas cookie, and their happiest "ho ho ho". Cheer for each one, then declare them an honorary elf.',
  'HELP ME SORT A LETTER: you have a funny letter in your hand (from Rudolph, who wants a scarf for his nose, or from a penguin who got lost). Read it out and ask the child where to send it: Santa\'s desk, the workshop or the reindeer stable. Be delighted with their idea.',
  'THE JINGLE CHECK: the workshop needs a jingle test. Ask the child to sing a little jingle or make a jingle sound, and say the lamps on your switchboard lit up from it.',
  'THE ELF RIDDLE: ask one easy riddle that fits the child\'s age (for example: "I am white, I am cold, I fall from the sky. What am I?"). Give a giggly hint if needed and celebrate the answer, right or not.',
  'COUNT THE SLEEPS: count the sleeps until the presents come together with the child, out loud, in a fun way (if it\'s a big number, count by tens or just the last few).',
];

export const ELF_QUIRKS = [
  'Your bell on your hat jingles whenever you nod, which makes you giggle.',
  'Your best friend Tiitu the elf keeps waving at you through the window.',
  'Santa\'s cat Pipari keeps trying to steal your stamps.',
  'You love hot cocoa with exactly seven marshmallows.',
  'You once plugged Santa into the reindeer stable by mistake and he talked to Dasher for five minutes.',
  'Your feather quill writes all by itself, and sometimes it gets ahead of you.',
];

export const ELF_GOODBYES = [
  'another call is blinking on the switchboard',
  'the letter sack is about to tip over again',
  'it\'s time for the elves\' cocoa break',
  'Santa is ringing from his cabin',
];

export function planElfCall() {
  const pick = <T>(a: T[]) => a[Math.floor(Math.random() * a.length)];
  const n = 1000 + Math.floor(Math.random() * 9000);
  const quirks = [...ELF_QUIRKS].sort(() => Math.random() - 0.5).slice(0, 2);
  return { opening: pick(ELF_OPENINGS).replace('{n}', String(n)), game: pick(ELF_GAMES), quirks, goodbye: pick(ELF_GOODBYES), connection: n };
}
export type ElfPlan = ReturnType<typeof planElfCall>;
